import classNames from 'classnames'

import { EMPTY_OBJECT } from '../../../constants/common'
import { isApiNotFoundStatus } from '../../../utils/api'
import { filterPermittedActionsWithDividers } from '../../../utils/massActions'
import { isSubArchive } from '../../../utils/route'
import { isExternalContactUser } from '../../../utils/user'
import {
  EMAIL_PROFILE_ACTION_ADD_NOTE,
  EMAIL_PROFILE_ACTION_ARCHIVE,
  EMAIL_PROFILE_ACTION_DOWNLOAD_FILES,
  EMAIL_PROFILE_ACTION_MARK_AS_IMPORTANT,
  EMAIL_PROFILE_ACTION_MARK_AS_UNIMPORTANT,
  EMAIL_PROFILE_ACTION_MARK_AS_UNREAD,
  EMAIL_PROFILE_ACTION_PRINT,
  EMAIL_PROFILE_ACTION_RESTORE,
} from '../Email.constants'

import * as styles from './EmailProfile.module.css'

import ArchiveIcon from '../../../icons/archive.svg'
import EmailUnreadIcon from '../../../icons/email-unread.svg'
import ExportIcon from '../../../icons/export.svg'
import PrintIcon from '../../../icons/print.svg'
import RestoreIcon from '../../../icons/restore.svg'
import StarEmptyIcon from '../../../icons/star.svg'
import StarFilledIcon from '../../../icons/star-filled.svg'

function toWidgetParticipantUser(user) {
  return {
    ...user,
    ...(isExternalContactUser(user)
      ? { email: null, fullname: user.email }
      : null),
  }
}

export function isEmailProfileUnavailable({
  isNavigationEnabled,
  serverError,
}) {
  return (
    Boolean(isNavigationEnabled) && isApiNotFoundStatus(serverError?.status)
  )
}

export function getEmailProfileNextUpdatedData(prevData, data) {
  return { ...prevData, message: data }
}

export function getEmailProfileSkeletonClassName(...componentClassnames) {
  return classNames(styles.skeleton, ...componentClassnames)
}

export function getEmailProfilePreparedWidgetsData(data) {
  return {
    ...data,
    copiedTo: {
      ...data.archive_copy_entities,
      external_contacts: data.archive_copy_entities?.external_contacts.map(
        toWidgetParticipantUser
      ),
    },
    participants: {
      items: data.participants.items.map(toWidgetParticipantUser),
    },
  }
}

export function getEmailProfileActionOptionMarkAsUnstarred({ permissions, t }) {
  return {
    label: t('Emails.MarkAsUnstarredActionOptionKey'),
    permission: permissions.can_edit_starred,
    value: EMAIL_PROFILE_ACTION_MARK_AS_UNIMPORTANT,
  }
}

export function getEmailProfileActionOptionMarkAsStarred({ permissions, t }) {
  return {
    label: t('Emails.MarkAsStarredActionOptionKey'),
    permission: permissions.can_edit_starred,
    value: EMAIL_PROFILE_ACTION_MARK_AS_IMPORTANT,
  }
}

export function getEmailProfileActionOptionAddNote({ permissions, t }) {
  return {
    label: t('Emails.AddNoteActionOptionKey'),
    permission: permissions.can_edit_note,
    value: EMAIL_PROFILE_ACTION_ADD_NOTE,
  }
}

export function getEmailProfileActionOptionMarkAsUnread({ permissions, t }) {
  return {
    label: t('Emails.MarkAsUnreadActionOptionKey'),
    permission: permissions.can_edit_unread_status,
    value: EMAIL_PROFILE_ACTION_MARK_AS_UNREAD,
  }
}

export function getEmailProfileActionOptionDownloadFiles({ files, t }) {
  return {
    label: t('Emails.DownloadFilesActionOptionKey'),
    permission: files?.length > 0,
    value: EMAIL_PROFILE_ACTION_DOWNLOAD_FILES,
  }
}

export function getEmailProfileActionOptionPrint(t) {
  return {
    label: t('Emails.PrintActionOptionKey'),
    permission: true,
    value: EMAIL_PROFILE_ACTION_PRINT,
  }
}

export function getEmailProfileActionOptionArchive({ permissions, sub, t }) {
  return {
    label: t('Emails.ArchiveActionOptionKey'),
    permission: permissions.can_archive && !isSubArchive(sub),
    value: EMAIL_PROFILE_ACTION_ARCHIVE,
  }
}

export function getEmailProfileActionOptionRestore({ permissions, sub, t }) {
  return {
    label: t('Emails.RestoreActionOptionKey'),
    permission: permissions.can_restore && isSubArchive(sub),
    value: EMAIL_PROFILE_ACTION_RESTORE,
  }
}

export function getEmailProfileActionsWidgetItems({
  entityData,
  sub,
  t,
  widgetsData,
}) {
  const { permissions = EMPTY_OBJECT, starred } = entityData || EMPTY_OBJECT

  const items = [
    starred
      ? {
          ...getEmailProfileActionOptionMarkAsUnstarred({ permissions, t }),
          beforeIconProps: { Icon: StarFilledIcon },
          tooltip: t('EmailProfile.ActionsWidgetMarkAsUnstarredTooltipKey'),
        }
      : {
          ...getEmailProfileActionOptionMarkAsStarred({ permissions, t }),
          beforeIconProps: { Icon: StarEmptyIcon },
          tooltip: t('EmailProfile.ActionsWidgetMarkAsStarredTooltipKey'),
        },
    {
      ...getEmailProfileActionOptionMarkAsUnread({ permissions, t }),
      beforeIconProps: { Icon: EmailUnreadIcon },
      tooltip: t('EmailProfile.ActionsWidgetMarkAsUnreadTooltipKey'),
    },
    {
      ...getEmailProfileActionOptionDownloadFiles({
        files: widgetsData?.files.items || entityData?.files,
        t,
      }),
      beforeIconProps: { Icon: ExportIcon },
      tooltip: t('EmailProfile.ActionsWidgetDownloadAllFilesTooltipKey'),
    },
    {
      ...getEmailProfileActionOptionPrint(t),
      beforeIconProps: { Icon: PrintIcon },
      tooltip: t('EmailProfile.ActionsWidgetPrintTooltipKey'),
    },
    {
      ...getEmailProfileActionOptionArchive({ permissions, sub, t }),
      beforeIconProps: { Icon: ArchiveIcon },
      tooltip: t('EmailProfile.ActionsWidgetArchiveTooltipKey'),
    },
    {
      ...getEmailProfileActionOptionRestore({ permissions, sub, t }),
      beforeIconProps: { Icon: RestoreIcon },
      tooltip: t('EmailProfile.ActionsWidgetRestoreTooltipKey'),
    },
  ]

  return filterPermittedActionsWithDividers(items)
}
